import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { useAuth } from "./AuthContext";

const DataContext = createContext(null);
export const useData = () => useContext(DataContext);

const COLLS = ["events", "tasks", "guests", "expenses", "email_templates", "agenda_days", "activities", "collaborators"];

export function DataProvider({ children }) {
  const { user } = useAuth();
  const authed = user && typeof user === "object";

  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [guests, setGuests] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [agendaDays, setAgendaDays] = useState([]);
  const [activities, setActivities] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [profile, setProfileState] = useState({});
  const [company, setCompanyState] = useState({});
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const setters = { events: setEvents, tasks: setTasks, guests: setGuests, expenses: setExpenses,
    email_templates: setEmailTemplates, agenda_days: setAgendaDays, activities: setActivities, collaborators: setCollaborators };

  const reloadAll = useCallback(async () => {
    if (!authed) return;
    try {
      const results = await Promise.all(COLLS.map((c) => client.get(`/data/${c}`)));
      COLLS.forEach((c, i) => setters[c](results[i].data || []));
      const [p, comp] = await Promise.all([client.get("/profile"), client.get("/company")]);
      setProfileState(p.data || {});
      setCompanyState(comp.data || {});
      setEvents((prev) => {
        if (prev.length && !selectedEventId) setSelectedEventId(prev[0].id);
        return prev;
      });
      setLoaded(true);
    } catch (e) {
      setLoaded(true);
    }
    // eslint-disable-next-line
  }, [authed]);

  useEffect(() => {
    if (authed) reloadAll();
    else { setLoaded(false); }
  }, [authed, reloadAll]);

  useEffect(() => {
    if (!selectedEventId && events.length) setSelectedEventId(events[0].id);
    if (selectedEventId && events.length && !events.find((e) => e.id === selectedEventId)) {
      setSelectedEventId(events[0]?.id || null);
    }
  }, [events, selectedEventId]);

  const makeCrud = (coll, setter) => ({
    add: async (obj) => { const { data } = await client.post(`/data/${coll}`, obj); setter((p) => [...p, data]); return data; },
    update: async (id, obj) => { const { data } = await client.put(`/data/${coll}/${id}`, obj); setter((p) => p.map((x) => (x.id === id ? data : x))); return data; },
    remove: async (id) => { await client.delete(`/data/${coll}/${id}`); setter((p) => p.filter((x) => x.id !== id)); },
  });

  const eventsCrud = makeCrud("events", setEvents);

  const addEvent = async (obj) => { const created = await eventsCrud.add(obj); const r = await client.get("/data/tasks"); setTasks(r.data || []); setSelectedEventId(created.id); return created; };
  const deleteEvent = async (id) => { await client.delete(`/data/events/${id}`); await reloadAll(); if (selectedEventId === id) setSelectedEventId(null); };

  const value = {
    events, tasks, guests, expenses, emailTemplates, agendaDays, activities, collaborators,
    profile, company, selectedEventId, setSelectedEventId, loaded, reloadAll,
    getEvent: (id) => events.find((e) => e.id === id),
    selectedEvent: events.find((e) => e.id === selectedEventId) || null,

    addEvent, updateEvent: eventsCrud.update, deleteEvent,
    ...mapCrud("Task", makeCrud("tasks", setTasks)),
    ...mapCrud("Guest", makeCrud("guests", setGuests)),
    ...mapCrud("Expense", makeCrud("expenses", setExpenses)),
    ...mapCrud("EmailTemplate", makeCrud("email_templates", setEmailTemplates)),
    ...mapCrud("AgendaDay", makeCrud("agenda_days", setAgendaDays)),
    ...mapCrud("Activity", makeCrud("activities", setActivities)),
    ...mapCrud("Collaborator", makeCrud("collaborators", setCollaborators)),

    tasksByEvent: (eid) => tasks.filter((t) => t.eventId === eid),
    guestsByEvent: (eid) => guests.filter((g) => g.eventId === eid),
    expensesByEvent: (eid) => expenses.filter((x) => x.eventId === eid),
    templatesByEvent: (eid) => emailTemplates.filter((t) => t.eventId === eid),
    daysByEvent: (eid) => agendaDays.filter((d) => d.eventId === eid),
    activitiesByDay: (did) => activities.filter((a) => a.dayId === did),

    saveProfile: async (obj) => { const { data } = await client.put("/profile", obj); setProfileState(data); return data; },
    saveCompany: async (obj) => { const { data } = await client.put("/company", obj); setCompanyState(data); return data; },
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

function mapCrud(name, crud) {
  return {
    [`add${name}`]: crud.add,
    [`update${name}`]: crud.update,
    [`delete${name}`]: crud.remove,
  };
}
